// Local diagram annotations are independent of authoritative online games.
import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";
import type { GatewayClient } from "../core/client.js";
import type { SessionStore } from "../core/store.js";
import { BoardLines } from "./Board.js";

type Cell = "B" | "W" | null;
const storageKey = "404weiqi.local-diagram.v1";
function loadDiagram(): Cell[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "null");
    if (Array.isArray(value) && value.length === 361 && value.every(c => c === null || c === "B" || c === "W")) return value;
  } catch { /* Private browsing or invalid data must not prevent editing. */ }
  return Array<Cell>(361).fill(null);
}

export function OfflineShell({ client, store, onlineApp }: {
  client: GatewayClient; store: SessionStore; onlineApp: ReactNode;
}) {
  const [history, setHistory] = useState<Cell[][]>(() => [loadDiagram()]);
  const [cursor, setCursor] = useState(0);
  const [brush, setBrush] = useState<Cell>("B");
  const [online, setOnline] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [notice, setNotice] = useState("GPU 连接默认关闭。棋盘保存在此浏览器中。");
  const attempt = useRef(0);
  const board = history[cursor]!;
  useEffect(() => { try { localStorage.setItem(storageKey, JSON.stringify(board)); } catch { setNotice("浏览器无法保存棋盘，请导出摆放。"); } }, [board]);
  useEffect(() => () => { attempt.current++; client.closeSocket(); }, [client]);
  function edit(next: Cell[]) {
    setHistory([...history.slice(0, cursor + 1), next]); setCursor(cursor + 1);
  }
  function disconnect() {
    attempt.current++;
    try { if (store.phase === "live") client.analysisStop(); } catch { /* Already closed. */ } finally { client.closeSocket(); setOnline(false); setConnecting(false); }
    setNotice("GPU 已断开，继续本地摆谱。");
  }
  async function connect() {
    const current = ++attempt.current;
    setConnecting(true); setNotice("正在连接 GPU 服务…");
    const timer = window.setTimeout(() => {
      if (attempt.current !== current) return;
      attempt.current++; client.closeSocket(); setOnline(false); setConnecting(false);
      setNotice("GPU 服务连接超时，棋盘已保留。可稍后手动重试。");
    }, 12000);
    try {
      await client.connect();
      if (attempt.current === current) { setOnline(true); setNotice("GPU 服务已连接；本地摆谱与在线对局分别保存。"); }
    } catch {
      if (attempt.current === current) {
        client.closeSocket(); setOnline(false);
        setNotice("GPU 服务暂不可用，棋盘已保留。可稍后手动重试。");
      }
    } finally { window.clearTimeout(timer); if (attempt.current === current) setConnecting(false); }
  }
  function download() {
    const points = (color: Cell) => board.flatMap((c, i) => c === color ? [`[${String.fromCharCode(97 + i % 19)}${String.fromCharCode(97 + Math.floor(i / 19))}]`] : []).join("");
    const black = points("B"), white = points("W");
    const sgf = `(;FF[4]GM[1]SZ[19]CA[UTF-8]C[Local diagram setup]${black ? "AB" + black : ""}${white ? "AW" + white : ""})`;
    const url = URL.createObjectURL(new Blob([sgf], { type: "application/x-go-sgf" }));
    const link = document.createElement("a"); link.href = url; link.download = "404weiqi-diagram.sgf"; link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <>
    <section style={{maxWidth: 960, margin: "16px auto", padding: 16}}>
      <h1>404weiqi · 摆谱</h1>
      <p role="status">{notice}</p>
      <button onClick={online || connecting ? disconnect : () => { void connect(); }}>{connecting ? "取消连接" : online ? "断开 GPU · 返回摆谱" : "连接 GPU 服务"}</button>
      <div hidden={online}>
        <p>自由摆放黑白棋子；擦除、撤销或导出当前摆放。此模式不自动提子、判定合法性或计分。</p>
        <div style={{display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0"}}>
          <button aria-pressed={brush === "B"} onClick={() => setBrush("B")}>黑子</button>
          <button aria-pressed={brush === "W"} onClick={() => setBrush("W")}>白子</button>
          <button aria-pressed={brush === null} onClick={() => setBrush(null)}>擦除</button>
          <button disabled={cursor === 0} onClick={() => setCursor(cursor - 1)}>撤销</button>
          <button disabled={cursor === history.length - 1} onClick={() => setCursor(cursor + 1)}>重做</button>
          <button onClick={() => { if (window.confirm("清空当前摆放？可通过撤销恢复。")) edit(Array<Cell>(361).fill(null)); }}>清空</button>
          <button onClick={download}>导出 SGF 摆放</button>
        </div>
        <div className="board-wrap"><div className="board-stage">
          <div className="board size-19" style={{"--board-size": 19, gridTemplateColumns: "repeat(19, 1fr)"} as CSSProperties} data-board-size="19" aria-label="本地摆谱棋盘">
            <BoardLines size={19} />
            {board.map((cell, index) => <button key={index} className={`cell${cell === "B" ? " black" : cell === "W" ? " white" : ""}`} aria-label={`${String.fromCharCode(65 + index % 19)}${Math.floor(index / 19) + 1} ${cell === "B" ? "黑" : cell === "W" ? "白" : "空"}`} onClick={() => {
              if (board[index] === brush) return;
              const next = board.slice(); next[index] = brush; edit(next);
            }}><span className="stone" aria-hidden="true">{cell === "B" ? "●" : cell === "W" ? "○" : ""}</span></button>)}
          </div>
        </div></div>
      </div>
    </section>
    {online && onlineApp}
  </>;
}
